import {
  Button,
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Form,
  Input,
  Label,
  Container,
  Row,
  Col,
  Spinner,
} from "reactstrap";
import { useEffect, useMemo, useRef, useState } from "react";
import UserHeader from "components/Headers/UserHeader.js";
import { auth, db, storage } from "data/firebase";
import { doc, deleteDoc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { onAuthStateChanged, deleteUser } from "firebase/auth";
import { getAuthUser, clearAuth, setAuthUser } from "components/Auth/authStore";
import { useNavigate } from "react-router-dom";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { getCountries, getCountryCallingCode } from "libphonenumber-js";
import { toE164TR, toFriendlyPhoneAuthError, usePhoneOtpFirebase } from "components/Auth/usePhoneOtpFirebase";

const GOREV_LISTESI = [
  "",
  "Yazılım Geliştirici",
  "Grafik Tasarımcı",
  "Proje Yöneticisi",
  "Pazarlama Uzmanı",
  "İnsan Kaynakları Uzmanı",
  "Muhasebeci / Mali Müşavir",
  "Avukat",
  "Mimar",
  "Mühendis",
  "Doktor",
  "Hemşire",
  "Eczacı",
  "Öğretmen / Akademisyen",
  "Gazeteci / Medya",
  "Çiftçi / Tarım Uzmanı",
  "Turizmci / Otelci",
  "Aşçı / Şef",
  "Esnaf / Tüccar",
  "Devlet Memuru",
  "Serbest Meslek",
  "Emekli",
  "Öğrenci",
  "Diğer",
];

const EMPTY_PROFILE = {
  fullName: "",
  gorev: "",
  email: "",
  address: "",
  photoURL: "",
  publishProfileOnLegends: false,
  legendShareFullName: false,
  legendShareGorev: false,
  legendShareEmail: false,
  legendSharePhone: false,
  legendShareAddress: false,
  legendSharePhoto: false,
};

const COUNTRY_OPTIONS = ["TR", ...getCountries().filter((c) => c !== "TR").sort()];

const phoneKey = (value) => String(value ?? "").replace(/[^0-9]/g, "");

const resizeImage = (file, maxDim = 800) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const ratio = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.8);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  });

const Profile = () => {
  const [firebaseUser, setFirebaseUser] = useState(() => auth.currentUser);
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [previewURL, setPreviewURL] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const localUser = useMemo(() => getAuthUser(), []);
  const profileDocId = firebaseUser?.uid || localUser?.id || null;
  const profileRef = useMemo(() => {
    if (!profileDocId) return null;
    return doc(db, "userProfiles", profileDocId);
  }, [profileDocId]);

  const displayTitle = profile.fullName || "Profil";
  const displayGorev = profile.gorev || "";
  const displaySubtitle =
    profile.email ||
    firebaseUser?.phoneNumber ||
    localUser?.phone ||
    "";

  const hasPhone = Boolean(firebaseUser?.phoneNumber || localUser?.phone || profile?.phone);

  const [phoneCountry, setPhoneCountry] = useState("TR");
  const [phoneNationalNumber, setPhoneNationalNumber] = useState("");
  const [phoneSmsCode, setPhoneSmsCode] = useState("");
  const [phoneStep, setPhoneStep] = useState("phone");
  const [phoneError, setPhoneError] = useState("");
  const [phoneSuccess, setPhoneSuccess] = useState("");
  const [isPhoneLoading, setIsPhoneLoading] = useState(false);

  const phoneOtp = usePhoneOtpFirebase({
    auth,
    recaptchaButtonId: "profile-phone-link-button",
    mode: "link",
    user: firebaseUser,
  });

  const dialCode = (() => {
    try {
      return `+${getCountryCallingCode(phoneCountry)}`;
    } catch {
      return "+90";
    }
  })();

  const phoneE164 = toE164TR({ dialCode, nationalNumber: phoneNationalNumber });

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setFirebaseUser(u));
    return () => unsub();
  }, []);

  useEffect(() => {
    if (hasPhone) return;
    (async () => {
      try {
        await phoneOtp.initRecaptcha();
      } catch {}
    })();
  }, [hasPhone, phoneOtp]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setError("");

      if (!profileRef) {
        setLoading(false);
        setProfile(EMPTY_PROFILE);
        return;
      }

      setLoading(true);
      try {
        const snap = await getDoc(profileRef);
        if (cancelled) return;

        if (snap.exists()) {
          const data = snap.data() || {};
          setProfile({ ...EMPTY_PROFILE, ...data });
        } else {
          const seed = {
            ...EMPTY_PROFILE,
            uid: firebaseUser?.uid || null,
            phone: firebaseUser?.phoneNumber || localUser?.phone || null,
            provider: localUser?.provider || "unknown",
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          };
          await setDoc(profileRef, seed, { merge: true });
          if (!cancelled) setProfile(EMPTY_PROFILE);
        }
      } catch {
        if (!cancelled) {
          setError(
            "Profil bilgileri Firebase'den alınamadı. Firestore kurallarını ve bağlantıyı kontrol edin."
          );
          setProfile(EMPTY_PROFILE);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [profileRef, firebaseUser?.uid, firebaseUser?.phoneNumber, localUser?.phone, localUser?.provider]);

  const onChangeField = (key) => (e) => {
    const value = e?.target?.value ?? "";
    setProfile((prev) => ({ ...prev, [key]: value }));
  };

  const onToggleField = (key) => (e) => {
    const checked = Boolean(e?.target?.checked);
    setProfile((prev) => ({ ...prev, [key]: checked }));
  };

  const onPhotoSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Lütfen bir resim dosyası seçin.");
      return;
    }

    setError("");
    setSelectedFile(file);
    const objectUrl = URL.createObjectURL(file);
    setPreviewURL(objectUrl);
    if (fileInputRef.current)
      fileInputRef.current.value = "";
  };

  const onPhotoUpload = async () => {
    if (!selectedFile) {
      setError("Önce bir fotoğraf seçin.");
      return;
    }
    if (!profileDocId) {
      setError("Fotoğraf yüklenemedi: kullanıcı bulunamadı.");
      return;
    }

    setError("");
    setUploading(true);
    try {
      const resized = await resizeImage(selectedFile);
      const photoRef = storageRef(
        storage,
        `profilePhotos/${profileDocId}/avatar.jpg`
      );
      await uploadBytes(photoRef, resized, { contentType: "image/jpeg" });
      const url = await getDownloadURL(photoRef);
      setProfile((prev) => ({ ...prev, photoURL: url }));

      if (profileRef) {
        await setDoc(
          profileRef,
          { photoURL: url, updatedAt: serverTimestamp() },
          { merge: true }
        );
      }

      setSelectedFile(null);
      if (previewURL) URL.revokeObjectURL(previewURL);
      setPreviewURL(null);
      setSuccess("Fotoğraf başarıyla yüklendi.");
      setTimeout(() => setSuccess(""), 3000);
    } catch {
      setError(
        "Fotoğraf yüklenemedi. Firebase Storage kurallarını kontrol edin."
      );
    } finally {
      setUploading(false);
    }
  };

  const onDeleteAccount = async () => {
    const confirmed = window.confirm(
      "Hesabınızı silmek istediğinize emin misiniz?\n\nBu işlem geri alınamaz. Tüm profil verileriniz ve fotoğrafınız kalıcı olarak silinecektir."
    );
    if (!confirmed) return;

    setError("");
    setDeleting(true);
    try {
      if (profileRef) {
        await deleteDoc(profileRef);
      }

      const user = auth.currentUser;
      if (user) {
        await deleteUser(user);
      }

      clearAuth();
      navigate("/auth/login", { replace: true });
    } catch (e) {
      const code = e?.code || "";
      if (code.includes("requires-recent-login")) {
        setError(
          "Hesap silmek için yakın zamanda giriş yapmış olmanız gerekir. Lütfen çıkış yapıp tekrar giriş yaptıktan sonra deneyin."
        );
      } else {
        setError("Hesap silinemedi. Lütfen tekrar deneyin.");
      }
    } finally {
      setDeleting(false);
    }
  };

  const onSave = async (e) => {
    e?.preventDefault?.();
    setError("");
    setSuccess("");

    if (!profileRef) {
      setError("Profil kaydedilemedi: kullanıcı bulunamadı.");
      return;
    }

    setSaving(true);
    try {
      await setDoc(
        profileRef,
        {
          ...EMPTY_PROFILE,
          ...profile,
          uid: firebaseUser?.uid || null,
          phone: firebaseUser?.phoneNumber || localUser?.phone || null,
          provider: localUser?.provider || "unknown",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setSuccess("Profil başarıyla kaydedildi.");
      setTimeout(() => setSuccess(""), 3000);
    } catch {
      setError(
        "Profil kaydedilemedi. Firestore yazma izinlerini (rules) kontrol edin."
      );
    } finally {
      setSaving(false);
    }
  };

  const onSendPhoneCode = async () => {
    setPhoneError("");
    setPhoneSuccess("");

    if (hasPhone) return;
    if (!firebaseUser?.uid) {
      setPhoneError("Telefon eklemek için oturum açmış olmalısınız.");
      return;
    }
    if (!phoneE164) {
      setPhoneError("Lütfen telefon numaranızı girin.");
      return;
    }

    setIsPhoneLoading(true);
    try {
      await phoneOtp.sendCode(phoneE164);
      setPhoneStep("code");
      setPhoneSuccess("Doğrulama kodu SMS ile gönderildi.");
    } catch (e) {
      const code = e?.code ? String(e.code) : "";
      setPhoneError(`${toFriendlyPhoneAuthError(e)}${code ? ` (Hata: ${code})` : ""}`);
    } finally {
      setIsPhoneLoading(false);
    }
  };

  const onVerifyPhoneCode = async () => {
    setPhoneError("");
    setPhoneSuccess("");

    if (!firebaseUser?.uid) {
      setPhoneError("Telefon doğrulanamadı: kullanıcı bulunamadı.");
      return;
    }

    const code = String(phoneSmsCode ?? "").trim();
    if (!code) {
      setPhoneError("Lütfen SMS doğrulama kodunu girin.");
      return;
    }

    setIsPhoneLoading(true);
    try {
      const credential = await phoneOtp.verifyCode(code);
      const linkedUser = credential?.user || auth.currentUser;
      const linkedPhone = linkedUser?.phoneNumber || phoneE164;

      if (profileRef) {
        await setDoc(
          profileRef,
          { phone: linkedPhone, updatedAt: serverTimestamp() },
          { merge: true }
        );
      }

      await setDoc(
        doc(db, "users", firebaseUser.uid),
        { uid: firebaseUser.uid, phone: linkedPhone, updatedAt: serverTimestamp() },
        { merge: true }
      );

      await setDoc(
        doc(db, "usersByPhone", phoneKey(linkedPhone)),
        { uid: firebaseUser.uid, phone: linkedPhone, updatedAt: serverTimestamp() },
        { merge: true }
      );

      setAuthUser({
        ...(getAuthUser() || {}),
        id: firebaseUser.uid,
        phone: linkedPhone,
      });

      setProfile((prev) => ({ ...prev, phone: linkedPhone }));
      setPhoneSuccess("Telefon başarıyla eklendi.");
      setPhoneStep("phone");
      setPhoneSmsCode("");
      setPhoneNationalNumber("");
      try {
        phoneOtp.clearRecaptcha();
      } catch {}
    } catch (e) {
      const code = e?.code ? String(e.code) : "";
      setPhoneError(`Kod doğrulanamadı.${code ? ` (Hata: ${code})` : ""}`);
    } finally {
      setIsPhoneLoading(false);
    }
  };

  const avatarSrc = previewURL || profile.photoURL || require("../../assets/img/theme/default-avatar.svg").default || require("../../assets/img/theme/default-avatar.svg");

  return (
    <>
      <UserHeader />
      {}
      <Container className="mt--7" fluid>
        <Row>
          {}
          <Col className="order-xl-2 mb-5 mb-xl-0" xl="4">
            <Card className="card-profile shadow">
              <Row className="justify-content-center">
                <Col className="order-lg-2" lg="3">
                  <div className="card-profile-image">
                    <a
                      href="#photo"
                      onClick={(e) => {
                        e.preventDefault();
                        fileInputRef.current?.click();
                      }}
                      title="Fotoğraf değiştirmek için tıklayın"
                    >
                      <img
                        alt="Profil"
                        className="rounded-circle"
                        src={avatarSrc}
                        style={{ width: 180, height: 180, objectFit: "cover" }}
                      />
                    </a>
                  </div>
                </Col>
              </Row>

              <CardHeader className="text-center border-0 pt-8 pt-md-8 pb-0 pb-md-4">
                <div className="d-flex justify-content-center flex-wrap" style={{ gap: 8 }}>
                  <Button
                    color="default"
                    outline
                    size="sm"
                    disabled={uploading}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <i className="ni ni-camera-compact mr-1" />
                    Fotoğraf Seç
                  </Button>
                  {selectedFile ? (
                    <Button
                      color="info"
                      size="sm"
                      disabled={uploading}
                      onClick={onPhotoUpload}
                    >
                      {uploading ? (
                        <>
                          <Spinner size="sm" className="mr-1" /> Yükleniyor…
                        </>
                      ) : (
                        <>
                          <i className="ni ni-cloud-upload-96 mr-1" />
                          Yükle
                        </>
                      )}
                    </Button>
                  ) : null}
                  {}
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={onPhotoSelect}
                  />
                </div>
                {previewURL && selectedFile ? (
                  <div className="text-center mt-2">
                    <small className="text-muted">
                      Önizleme — yüklemek için "Yükle" butonuna basın
                    </small>
                  </div>
                ) : null}

                <div className="mt-3 d-flex justify-content-center">
                  <Label
                    className="mb-0 d-flex align-items-start text-left"
                    style={{ cursor: loading ? "not-allowed" : "pointer", maxWidth: 420 }}
                  >
                    <Input
                      type="checkbox"
                      checked={Boolean(profile.legendSharePhoto)}
                      onChange={onToggleField("legendSharePhoto")}
                      disabled={loading}
                      className="mr-2"
                      style={{ marginTop: 2 }}
                    />
                    <span>Efsane İçeriğinde Bilgileriniz Görünsün mü?</span>
                  </Label>
                </div>
              </CardHeader>

              <CardBody className="pt-0 pt-md-4">
                <div className="text-center mt-md-5">
                  <h3>{displayTitle}</h3>
                  {displayGorev ? (
                    <div className="h5 text-muted font-weight-300">
                      <i className="ni ni-briefcase-24 mr-1" />
                      {displayGorev}
                    </div>
                  ) : null}
                  {displaySubtitle ? (
                    <div className="h5 font-weight-300">{displaySubtitle}</div>
                  ) : null}
                  <hr className="my-4" />
                  {loading ? (
                    <p>Yükleniyor…</p>
                  ) : profile.address ? (
                    <p>
                      <i className="ni ni-pin-3 mr-1" />
                      {profile.address}
                    </p>
                  ) : (
                    <p className="text-muted">Adres bilgisi eklenmemiş.</p>
                  )}
                </div>
              </CardBody>
            </Card>

            {}
            <Card className="shadow mt-4">
              <CardBody className="text-center py-3">
                <Button
                  color="danger"
                  outline
                  size="sm"
                  disabled={deleting}
                  onClick={onDeleteAccount}
                >
                  {deleting ? (
                    <>
                      <Spinner size="sm" className="mr-1" /> Siliniyor…
                    </>
                  ) : (
                    <>
                      <i className="ni ni-fat-remove mr-1" />
                      Hesabımı Sil
                    </>
                  )}
                </Button>
                <p className="text-muted small mt-2 mb-0">
                  Bu işlem geri alınamaz.
                </p>
              </CardBody>
            </Card>
          </Col>

          {}
          <Col className="order-xl-1" xl="8">
            <Card className="bg-secondary shadow">
              <CardHeader className="bg-white border-0">
                <Row className="align-items-center">
                  <Col xs="8">
                    <h3 className="mb-0">Profil Bilgileri</h3>
                  </Col>
                  <Col className="text-right" xs="4">
                    <Button
                      color="primary"
                      onClick={onSave}
                      size="lg"
                      disabled={saving || loading}
                      style={{ paddingLeft: 24, paddingRight: 24 }}
                    >
                      {saving ? "Kaydediliyor…" : "Kaydet"}
                    </Button>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                {error ? (
                  <div className="text-danger mb-3">
                    <small>{error}</small>
                  </div>
                ) : null}
                {success ? (
                  <div className="text-success mb-3">
                    <small>{success}</small>
                  </div>
                ) : null}

                <Form onSubmit={onSave}>
                  {}
                  <h6 className="heading-small text-muted mb-4">
                    Kişisel Bilgiler
                  </h6>
                  <div className="pl-lg-4">
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-fullname"
                          >
                            Adı Soyadı
                          </label>
                          <Input
                            className="form-control-alternative"
                            id="input-fullname"
                            placeholder="Adı Soyadı"
                            type="text"
                            value={profile.fullName}
                            onChange={onChangeField("fullName")}
                            disabled={loading}
                          />
                          <Label className="mb-0 mt-2 d-flex align-items-start" style={{ cursor: loading ? "not-allowed" : "pointer" }}>
                            <Input
                              type="checkbox"
                              checked={Boolean(profile.legendShareFullName)}
                              onChange={onToggleField("legendShareFullName")}
                              disabled={loading}
                              className="mr-2"
                              style={{ marginTop: 2 }}
                            />
                            <span>Efsane İçeriğinde Bilgileriniz Görünsün mü?</span>
                          </Label>
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-gorev"
                          >
                            Görevi
                          </label>
                          <Input
                            className="form-control-alternative"
                            id="input-gorev"
                            type="select"
                            value={profile.gorev}
                            onChange={onChangeField("gorev")}
                            disabled={loading}
                          >
                            <option value="">-- Seçiniz --</option>
                            {GOREV_LISTESI.filter(Boolean).map((g) => (
                              <option key={g} value={g}>
                                {g}
                              </option>
                            ))}
                          </Input>
                          <Label className="mb-0 mt-2 d-flex align-items-start" style={{ cursor: loading ? "not-allowed" : "pointer" }}>
                            <Input
                              type="checkbox"
                              checked={Boolean(profile.legendShareGorev)}
                              onChange={onToggleField("legendShareGorev")}
                              disabled={loading}
                              className="mr-2"
                              style={{ marginTop: 2 }}
                            />
                            <span>Efsane İçeriğinde Bilgileriniz Görünsün mü?</span>
                          </Label>
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-email"
                          >
                            E-posta
                          </label>
                          <Input
                            className="form-control-alternative"
                            id="input-email"
                            placeholder="ornek@eposta.com"
                            type="email"
                            value={profile.email}
                            onChange={onChangeField("email")}
                            disabled={loading}
                          />
                          <Label className="mb-0 mt-2 d-flex align-items-start" style={{ cursor: loading ? "not-allowed" : "pointer" }}>
                            <Input
                              type="checkbox"
                              checked={Boolean(profile.legendShareEmail)}
                              onChange={onToggleField("legendShareEmail")}
                              disabled={loading}
                              className="mr-2"
                              style={{ marginTop: 2 }}
                            />
                            <span>Efsane İçeriğinde Bilgileriniz Görünsün mü?</span>
                          </Label>
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">
                            Telefon
                          </label>
                          <Input
                            className="form-control-alternative"
                            type="text"
                            value={
                              firebaseUser?.phoneNumber ||
                              localUser?.phone ||
                              ""
                            }
                            disabled
                          />
                          <Label className="mb-0 mt-2 d-flex align-items-start" style={{ cursor: loading ? "not-allowed" : "pointer" }}>
                            <Input
                              type="checkbox"
                              checked={Boolean(profile.legendSharePhone)}
                              onChange={onToggleField("legendSharePhone")}
                              disabled={loading}
                              className="mr-2"
                              style={{ marginTop: 2 }}
                            />
                            <span>Efsane İçeriğinde Bilgileriniz Görünsün mü?</span>
                          </Label>
                        </FormGroup>
                      </Col>
                    </Row>

                    {!hasPhone ? (
                      <>
                        <hr className="my-4" />
                        <h6 className="heading-small text-muted mb-4">Telefon Ekle</h6>
                        {phoneError ? (
                          <div className="text-danger mb-3">
                            <small>{phoneError}</small>
                          </div>
                        ) : null}
                        {phoneSuccess ? (
                          <div className="text-success mb-3">
                            <small>{phoneSuccess}</small>
                          </div>
                        ) : null}

                        <Row>
                          <Col lg="6">
                            <FormGroup>
                              <label className="form-control-label" htmlFor="profile-phone">
                                Telefon
                              </label>
                              <div className="d-flex align-items-center" style={{ gap: 8 }}>
                                <select
                                  className="form-control form-control-alternative"
                                  aria-label="Ülke kodu"
                                  value={phoneCountry}
                                  onChange={(e) => setPhoneCountry(e.target.value)}
                                  disabled={isPhoneLoading}
                                  style={{ maxWidth: 140 }}
                                >
                                  {COUNTRY_OPTIONS.map((c) => (
                                    <option key={c} value={c}>
                                      {c} (+{getCountryCallingCode(c)})
                                    </option>
                                  ))}
                                </select>
                                <Input
                                  className="form-control-alternative"
                                  id="profile-phone"
                                  placeholder="530 XXX XX XX"
                                  type="tel"
                                  inputMode="tel"
                                  autoComplete="tel"
                                  value={phoneNationalNumber}
                                  onChange={(e) => setPhoneNationalNumber(e.target.value)}
                                  disabled={isPhoneLoading}
                                />
                              </div>
                            </FormGroup>
                          </Col>
                          <Col lg="6">
                            <FormGroup>
                              <label className="form-control-label" htmlFor="profile-phone-code">
                                SMS Kodu
                              </label>
                              <Input
                                className="form-control-alternative"
                                id="profile-phone-code"
                                placeholder="SMS Kodu"
                                type="text"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                value={phoneSmsCode}
                                onChange={(e) => setPhoneSmsCode(e.target.value)}
                                disabled={isPhoneLoading || phoneStep !== "code"}
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                          <Button
                            id="profile-phone-link-button"
                            color="info"
                            type="button"
                            disabled={isPhoneLoading}
                            onClick={phoneStep === "phone" ? onSendPhoneCode : onVerifyPhoneCode}
                          >
                            {phoneStep === "phone" ? "SMS Gönder" : "Doğrula ve Kaydet"}
                          </Button>
                          {phoneStep === "code" ? (
                            <Button
                              color="secondary"
                              type="button"
                              disabled={isPhoneLoading}
                              onClick={() => {
                                setPhoneStep("phone");
                                setPhoneSmsCode("");
                                setPhoneError("");
                                setPhoneSuccess("");
                              }}
                            >
                              Geri
                            </Button>
                          ) : null}
                        </div>
                      </>
                    ) : null}
                  </div>

                  <hr className="my-4" />

                  {}
                  <h6 className="heading-small text-muted mb-4">
                    Adres Bilgisi
                  </h6>
                  <div className="pl-lg-4">
                    <Row>
                      <Col md="12">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-address"
                          >
                            Adres
                          </label>
                          <Input
                            className="form-control-alternative"
                            id="input-address"
                            placeholder="Mahalle, sokak, no, ilçe / şehir"
                            type="textarea"
                            rows="3"
                            value={profile.address}
                            onChange={onChangeField("address")}
                            disabled={loading}
                          />
                          <Label className="mb-0 mt-2 d-flex align-items-start" style={{ cursor: loading ? "not-allowed" : "pointer" }}>
                            <Input
                              type="checkbox"
                              checked={Boolean(profile.legendShareAddress)}
                              onChange={onToggleField("legendShareAddress")}
                              disabled={loading}
                              className="mr-2"
                              style={{ marginTop: 2 }}
                            />
                            <span>Efsane İçeriğinde Bilgileriniz Görünsün mü?</span>
                          </Label>
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Profile;
